import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { HighlightBox } from './HighlightBox';
import { TableRowData } from './tableData';
import { isPositiveSavings } from './utils';

export const CostsTable = ({
  data,
  colWidths,
  hasSavingsHighlighted = false,
  hasTotalSavingsHighlighted = false,
}: {
  data: TableRowData[];
  colWidths: number[];
  hasSavingsHighlighted?: boolean;
  hasTotalSavingsHighlighted?: boolean;
}) => {
  const { t } = useTranslation();

  const HighlightedTableCell = ({ isHighlightedRow, value }: { isHighlightedRow: boolean; value?: string }) => (
    <TableCell sx={{ border: 'none' }}>
      <Typography
        variant={hasSavingsHighlighted ? 'subtitle2' : 'body2'}
        color={(theme) =>
          isPositiveSavings(value) && hasSavingsHighlighted
            ? theme.palette.success.main
            : hasSavingsHighlighted
              ? theme.palette.grey[600]
              : theme.palette.text.primary
        }
      >
        {isHighlightedRow && value ? <HighlightBox>{value}</HighlightBox> : value || ''}
      </Typography>
    </TableCell>
  );

  return (
    <Table id="costs-table" sx={{ tableLayout: 'fixed', width: '100%' }}>
      <colgroup>
        {colWidths?.map((width, index) => (
          <col key={index} style={{ width: `${width}%` }} />
        ))}
      </colgroup>

      <TableHead>
        <TableRow sx={{ borderBottom: (theme) => `1px solid ${theme.palette.grey[200]}` }}>
          <TableCell>
            <Typography variant="caption" fontWeight="600">
              {t('blueprints:details.table.costCategory')}
            </Typography>
          </TableCell>
          {[
            'blueprints:details.table.projectWithoutBattery',
            'blueprints:details.table.greenerHybridSetup',
            'blueprints:details.table.savingsAmount',
            'blueprints:details.table.savings',
          ].map((key) => (
            <TableCell key={key}>
              <Typography variant="caption" color="grey.600">
                {t(key)}
              </Typography>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      <TableBody>
        {data.map((row, index) => {
          const isLastRow = index === data.length - 1;
          const isBeforeLastRow = index === data.length - 2;
          const isHighlightedRow = isLastRow && hasTotalSavingsHighlighted;

          return (
            <TableRow
              key={index}
              sx={{
                borderBottom:
                  isLastRow || (isBeforeLastRow && hasSavingsHighlighted)
                    ? 'none'
                    : (theme) => `1px solid ${theme.palette.grey[200]}`,
                ...(isHighlightedRow
                  ? {
                      '& td': {
                        backgroundColor: 'action.hover',
                        border: 'none',
                      },
                      '& td:first-of-type': {
                        borderRadius: '6px 0 0 6px',
                      },
                      '& td:last-of-type': {
                        borderRadius: '0 6px 6px 0',
                      },
                    }
                  : {}),
              }}
            >
              <TableCell sx={{ border: 'none' }}>
                <Typography variant="body2" color="text.secondary">
                  {row.label}
                </Typography>
              </TableCell>

              <TableCell sx={{ border: 'none' }}>
                <Typography
                  variant={isHighlightedRow ? 'subtitle1' : 'body2'}
                  fontWeight={isHighlightedRow ? 600 : 400}
                >
                  {row.baseValue}
                </Typography>
              </TableCell>

              <TableCell sx={{ border: 'none' }}>
                <Typography
                  variant={isHighlightedRow ? 'subtitle1' : 'body2'}
                  fontWeight={isHighlightedRow ? 600 : 400}
                >
                  {row.hybridValue}
                </Typography>
              </TableCell>

              <HighlightedTableCell isHighlightedRow={isHighlightedRow} value={row.savingsAmount} />

              <HighlightedTableCell isHighlightedRow={isHighlightedRow} value={row.savings} />
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
